import { useState } from "react";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import type { AcessoDoSistema, RedePermitida } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import { useRemoverRede } from "@/api/consultas";
import { Botao } from "@/componentes/Botao";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao } from "@/componentes/Confirmacao";
import { dataHora, faz } from "@/formato";
import { AdicionarRede } from "./AdicionarRede";

/*
 * A aba Acesso — change add-restricao-de-ip, design D5.
 *
 * As redes de onde a aplicação pode chamar a API pública. Lista vazia libera
 * qualquer origem (decisão de 2026-10-25), e é por isso que o aviso de lista
 * vazia existe: numa aplicação de terceiro com chave `live`, "nenhuma rede" quer
 * dizer que a chave vazada emite de qualquer lugar do mundo.
 *
 * Quem calcula o aviso é o servidor (`aviso_sem_restricao`), e não a tela: ele
 * depende da modalidade da aplicação e do estado das chaves, e uma segunda
 * cópia da regra aqui divergiria dela no dia em que a modalidade mudar.
 */
export function AbaDeAcesso({
  sistemaID,
  sistemaNome,
  acesso,
}: {
  sistemaID: string;
  sistemaNome: string;
  /** `undefined` enquanto o detalhe carrega. */
  acesso: AcessoDoSistema | undefined;
}) {
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();
  const remover = useRemoverRede(cliente, sistemaID);
  const ehAdmin = pode("admin");

  // `null` fecha o formulário; a string é o que ele abre preenchido.
  const [formulario, setFormulario] = useState<string | null>(null);
  const [aRemover, setARemover] = useState<RedePermitida | null>(null);

  const redes = acesso?.redes ?? [];
  const recusa = acesso?.ultima_recusa ?? null;

  return (
    <>
      {acesso?.aviso_sem_restricao === true && (
        <p className="flex items-center gap-2 rounded-controle border border-transito/40 bg-transito/10 px-3 py-2 text-corpo text-transito">
          <AlertTriangle aria-hidden="true" className="size-4 shrink-0" />
          Qualquer origem com a chave live emite boleto.
        </p>
      )}

      {/*
        A última recusa é uma PISTA, e não um log: o registro é regravado no
        máximo uma vez por minuto por aplicação. Serve ao caso do parceiro que
        não sabe o próprio IP de saída — ele chama, é recusado, e o operador lê
        o endereço aqui.
      */}
      {recusa !== null && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-controle border border-fio bg-superficie-2 px-3 py-2">
          <p className="text-[12px] text-texto-2">
            última recusa: <span className="dados text-texto">{recusa.endereco}</span>
            {" · "}
            <span title={dataHora(recusa.em)}>{tempoDesde(recusa.em)}</span>
          </p>

          {ehAdmin && (
            <Botao tom="secundario" onClick={() => setFormulario(recusa.endereco)}>
              <Plus aria-hidden="true" className="size-4" />
              Adicionar este endereço
            </Botao>
          )}
        </div>
      )}

      {ehAdmin && (
        <div>
          <Botao tom="secundario" onClick={() => setFormulario("")}>
            <Plus aria-hidden="true" className="size-4" />
            Adicionar rede
          </Botao>
        </div>
      )}

      {redes.length === 0 ? (
        <p className="text-texto-2" title="Lista vazia: qualquer origem é aceita.">
          Nenhuma rede.
        </p>
      ) : (
        <ul aria-label="Redes permitidas" className="flex flex-col gap-2">
          {redes.map((rede) => (
            <li
              key={rede.id}
              className="flex items-start justify-between gap-2 rounded-controle border border-fio bg-superficie-2 p-3"
            >
              <div className="min-w-0">
                <p className="dados truncate text-texto">{rede.rede}</p>
                <p className="mt-[2px] truncate text-[12px] text-texto-2">{rede.descricao}</p>
                <p className="mt-1 text-[12px] text-texto-3">
                  {rede.criado_por_nome ?? "—"} · {dataHora(rede.criado_em)}
                </p>
              </div>

              {ehAdmin && (
                <Botao
                  tom="destrutivo"
                  aria-label={`Remover ${rede.rede}`}
                  onClick={() => setARemover(rede)}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                  Remover
                </Botao>
              )}
            </li>
          ))}
        </ul>
      )}

      {formulario !== null && (
        <AdicionarRede
          sistemaID={sistemaID}
          sistemaNome={sistemaNome}
          redeInicial={formulario}
          aoFechar={() => setFormulario(null)}
        />
      )}

      <Confirmacao
        pedido={
          aRemover === null
            ? null
            : {
                severidade: "destrutiva",
                titulo: `Remover ${aRemover.rede}?`,
                /*
                  Remover a ÚLTIMA rede não restringe mais: devolve a aplicação a
                  "qualquer origem". A API aceita — é o rollback da restrição —,
                  e por isso a consequência dita aqui é a oposta da do caso
                  comum.
                */
                corpo:
                  redes.length === 1
                    ? "A lista fica vazia: qualquer origem passa a ser aceita."
                    : "Chamadas desta rede passam a ser recusadas na hora.",
                verbo: "Remover rede",
              }
        }
        aoCancelar={() => setARemover(null)}
        aoConfirmar={async () => {
          if (aRemover === null) return;
          await remover.mutateAsync(aRemover.id);
          setARemover(null);
          avisos.feito("Rede removida", aRemover.rede);
        }}
      />
    </>
  );
}

/** "há 3 min", ou "agora" quando o relógio da máquina está atrás do servidor. */
function tempoDesde(iso: string): string {
  const quanto = faz(iso);
  return quanto === "agora" || quanto === "—" ? quanto : `há ${quanto}`;
}
